import { useEffect, useState } from 'react';
import { useAuthenticated, usePermissions } from 'react-admin';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import http from '../auth/http-common';
import { getAccessToken } from '../auth/helpers/auth';
import { NetworkPermissions } from './NetworkModules';
import { runSeparationTests, SeparationContext, TestResult } from '../helpers/separation-test';

export default function SeparationTest() {
  useAuthenticated();
  const { permissions } = usePermissions<NetworkPermissions>();
  const enabled = permissions?.role === 'ADMIN' && permissions.modules?.some(m => m.key === 'separation-test' && m.enabled);
  const [context, setContext] = useState<SeparationContext>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [foreignPlan, setForeignPlan] = useState('');
  const [results, setResults] = useState<TestResult[]>([]);
  useEffect(() => {
    let active = true;
    setContext(undefined);
    if (enabled) http.get('/admin/separation-test/context').then(r => { if (active) setContext(r.data); })
      .catch(() => { if (active) setError('Testmodulet kunne ikke indlæses. Log ind igen, eller kontrollér modultildelingen.'); });
    return () => { active = false; };
  }, [enabled]);
  const run = async () => {
    if (!context) return;
    setBusy(true); setResults([]);
    try {
      setResults(await runSeparationTests('admin', context, async (path, method, network) => {
        const token = getAccessToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
          method, headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(network ? { 'X-Business-Network': network } : {}) }, signal: AbortSignal.timeout(10000),
        });
        return response.status;
      }, foreignPlan));
    } finally { setBusy(false); }
  };
  return <Box sx={{ p: 3, maxWidth: 900 }}>
    <Typography component="h1" variant="h4" gutterBottom>Test af separation – admin</Typography>
    <Typography sx={{ mb: 2 }}>Kontrollér, at dit login kun har adgang til dit netværk og adminfunktionen. Testen ændrer ingen virksomhedsdata.</Typography>
    {!enabled && <Alert severity="info">Systemadministrator skal tildele modulet til dit netværk, og modulet skal aktiveres. Log ind som netværksadministrator for at teste.</Alert>}
    {error && <Alert severity="error">{error}</Alert>}
    {enabled && <Stack spacing={2}>
      <Typography>Netværk: {context?.network || 'Indlæser…'} · Rolle: {context?.role}</Typography>
      <TextField label="Plan-id fra et andet netværk (valgfrit)" value={foreignPlan} disabled={busy} onChange={e => { setForeignPlan(e.target.value); setResults([]); }} helperText="Brug et eksisterende id, som en anden testbruger har oplyst. Et ukendt id beviser ikke separation mellem netværk." />
      <Button variant="contained" disabled={busy || !context} onClick={run}>{busy ? 'Tester…' : 'Kør test'}</Button>
      <Typography>Grøn = forventet adgang eller afvisning. Rød = uventet svar. Sprunget over = endnu ikke kontrolleret. Testen er et øjebliksbillede; kør igen efter ændringer af adgang.</Typography>
      <div aria-live="polite">{results.map(r => <Alert key={r.name} severity={r.status === 'passed' ? 'success' : r.status === 'failed' ? 'error' : 'info'} sx={{ mb: 1 }}><strong>{r.status === 'passed' ? 'Bestået' : r.status === 'failed' ? 'Fejlet' : 'Sprunget over'}: {r.name}</strong><br />{r.detail}</Alert>)}</div>
    </Stack>}
  </Box>;
}
